import React, { useEffect, useState, useRef, Fragment } from "react";
import { Handles, Rail, Slider } from "react-compound-slider";
import IconFirst from "../../Icons/Temperature/First";
import IconLast from "../../Icons/Temperature/Last";
import IconNext from "../../Icons/Temperature/Next";
import IconPrevious from "../../Icons/Temperature/Previous";
import IconTomorrow from "../../Icons/Temperature/Tomorrow";
import IconYesterday from "../../Icons/Temperature/Yesterday";
import "./TemperatureSlider.css";
import { Handle, SliderRail } from "./TemperatureSliderComponents";

const sliderStyle: React.CSSProperties = {
  position: "relative",
  width: "100%",
  touchAction: "none",
};

type ButtonProps = {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  tooltip?: string;
};

function Button({
  onClick,
  children,
  disabled = false,
  tooltip = undefined,
}: ButtonProps) {
  return (
    <button
      className="temperature-slider-button"
      onClick={onClick}
      disabled={disabled}
      data-for="gray-tooltip"
      data-tip={tooltip}
      data-tip-disable={tooltip == undefined || disabled}
      data-effect="solid"
      data-place="top"
    >
      {children}
    </button>
  );
}

type Props = {
  min: number;
  max: number;
  value: number;
  onUpdated: (value: number) => void;
  onChanged: (value: number) => void;
  onButtonYesterdayClicked: () => void;
  onButtonTomorrowClicked: () => void;
  style?: React.CSSProperties;
  disableYesterday?: boolean;
  disableTomorrow?: boolean;
  handleWidth?: number;
  handleWidthPercentage?: number;
};

export default function TemperatureSlider({
  min,
  max,
  value,
  onUpdated,
  onChanged,
  onButtonYesterdayClicked,
  onButtonTomorrowClicked,
  style,
  disableYesterday = false,
  disableTomorrow = false,
  handleWidth = 30,
  handleWidthPercentage = undefined,
}: Props) {
  const domain: [number, number] = [min, max];
  const [sliderValue, setSliderValue] = useState(720);
  const [sliderWidth, setSliderWidth] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sliderRef.current) {
      setSliderWidth(sliderRef.current?.offsetWidth);
    }
  }, [sliderRef.current?.offsetWidth]);

  useEffect(() => {
    function handleResize() {
      if (sliderRef.current) {
        setSliderWidth(sliderRef.current?.offsetWidth);
      }
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if(value > 0){
      setSliderValue(value);
    }
  }, [value]);

  const onButtonLastClicked = () => {
    setSliderValue(min);
    onUpdated(min);
    onChanged(min);
  };

  const onButtonFirstClicked = () => {
    setSliderValue(max);
    onUpdated(max);
    onChanged(max);
  };

  const onButtonPreviousClicked = () => {
    const pos = sliderValue - 1;
    setSliderValue(pos);
    onUpdated(pos);
    onChanged(pos);
  };

  const onButtonNextClicked = () => {
    const pos = sliderValue + 1;
    setSliderValue(pos);
    onUpdated(pos);
    onChanged(pos);
  };

  return (
    <div
      className="temperature-slider-container"
      style={{
        ...style,
      }}
    >
      <div className="temperature-slider-button-left">
        <Button
          onClick={onButtonYesterdayClicked}
          disabled={disableYesterday}
          tooltip="Prev Day"
        >
          <IconYesterday />
        </Button>

        <Button onClick={onButtonLastClicked} tooltip="Day Start">
          <IconLast />
        </Button>

        <Button onClick={onButtonPreviousClicked} tooltip="Prev">
          <IconPrevious />
        </Button>
      </div>

      <div
        className="temperature-slider-slider"
        style={{
          paddingLeft: handleWidthPercentage
            ? `${(handleWidthPercentage * 100) / 2}%`
            : handleWidth / 2,
          paddingRight: handleWidthPercentage
            ? `${(handleWidthPercentage * 100) / 2}%`
            : handleWidth / 2,
        }}
        ref={sliderRef}
      >
        <Slider
          mode={1}
          step={1}
          domain={domain}
          rootStyle={sliderStyle}
          onUpdate={(values) => {
            setSliderValue(values[0]);
            onUpdated(values[0]);
          }}
          onChange={(values) => onChanged(values[0])}
          values={[sliderValue]}
          disabled={
            handleWidthPercentage !== undefined && handleWidthPercentage >= 1
          }
        >
          <Rail>
            {({ getRailProps }) => <SliderRail getRailProps={getRailProps} />}
          </Rail>
          <Handles>
            {({ handles, getHandleProps }) => (
              <div className="slider-handles">
                {handles.map((handle) => (
                  <Handle
                    key={handle.id}
                    handle={handle}
                    domain={domain}
                    width={
                      handleWidthPercentage
                        ? handleWidthPercentage * sliderWidth
                        : handleWidth
                    }
                    getHandleProps={getHandleProps}
                    disabled={false}
                    // widthPercentage={handleWidthPercentage ?? 100}
                    // maxWidth={sliderRef.current?.offsetWidth ?? 100}
                  />
                ))}
              </div>
            )}
          </Handles>
        </Slider>
      </div>

      <div className="temperature-slider-button-right">
        <Button onClick={onButtonNextClicked} tooltip="Next">
          <IconNext />
        </Button>

        <Button onClick={onButtonFirstClicked} tooltip="Day End">
          <IconFirst />
        </Button>

        <Button
          onClick={onButtonTomorrowClicked}
          disabled={disableTomorrow}
          tooltip="Next Day"
        >
          <IconTomorrow />
        </Button>
      </div>
    </div>
  );
}
